/**
 * The campaign door dialog — the three creation avenues (predefined / AI /
 * manual) for a paid campaign. Step 3 built the API; this is its UI.
 *
 * Same family as the broadcast door (components/paid-ads/broadcast-door):
 * shared chrome lives in ../door-shared.tsx (denied state, field errors,
 * territory targeting), the layout rhythm is identical — avenue chooser
 * cards, objective first, honest states everywhere. An agent who used one
 * door instantly knows the other.
 *
 * Avenues:
 *  - predefined → pick one of the market's active campaign templates; the
 *    template's targeting/objective/recommended spend ride along (Step-3
 *    copy semantics, source=predefined).
 *  - ai → the Step-5 chooser: the AI PROPOSES (objective/spend/territory
 *    only), the agent reviews and confirms; the confirm is the audited
 *    campaigns source=ai create. Nothing is created by the proposal.
 *  - manual → name + objective + spend + territory (source=manual).
 *
 * Honesty invariants (mirroring the broadcast door):
 *  - the Step-1 access gate denial (any layer) shows the machine code +
 *    message + upsell/admin copy — the templates fetch doubles as the gate
 *    probe, exactly like the broadcast door's spend-splits fetch;
 *  - caps violations (INVALID_BUDGET) land inline on the spend field;
 *  - everything created is a DRAFT — launch wiring is step 6 after F6.
 *
 * All creations are drafts. Web-only door: no backend changes.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, Rocket } from 'lucide-react'
import { api } from '@/api/client'
import type { CampaignTemplate, PaidMarket } from '@/api/paidAdsCampaigns'
import { useToast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useLocale } from '@/hooks/useLocale'
import { DoorDeniedState, DoorFieldError, DoorTargetingFields } from '../door-shared'
import { cdc, formatMicros } from './copy'

type Avenue = 'predefined' | 'ai' | 'manual'

export type CampaignDoorDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated?: (campaign: { id: string; source: string; status: string }) => void
}

const LAUNCH_PLATFORM = 'meta_ads'

export function CampaignDoorDialog({ open, onOpenChange, onCreated }: CampaignDoorDialogProps) {
  const { locale } = useLocale()
  const { addToast } = useToast()

  const [avenue, setAvenue] = useState<Avenue | null>(null)
  const [templates, setTemplates] = useState<CampaignTemplate[]>([])
  const [market, setMarket] = useState<PaidMarket | null>(null)
  const [selectedTemplateId, setSelectedTemplateId] = useState('')
  const [name, setName] = useState('')
  const [objective, setObjective] = useState('')
  const [budget, setBudget] = useState('')
  const [country, setCountry] = useState('')
  const [areas, setAreas] = useState('')
  const [radius, setRadius] = useState('')
  const [aiProposing, setAiProposing] = useState(false)
  const [aiNotice, setAiNotice] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [denied, setDenied] = useState<{ message: string; code?: string } | null>(null)
  const [loadingTemplates, setLoadingTemplates] = useState(false)
  const [creating, setCreating] = useState(false)
  const [created, setCreated] = useState(false)

  const reset = useCallback(() => {
    setAvenue(null)
    setSelectedTemplateId('')
    setName('')
    setObjective('')
    setBudget('')
    setCountry('')
    setAreas('')
    setRadius('')
    setAiProposing(false)
    setAiNotice(null)
    setFieldErrors({})
    setDenied(null)
    setCreating(false)
    setCreated(false)
  }, [])

  // Templates fetch doubles as the access-gate probe: a 402/403 here means a
  // cascade layer denied paid ads — show the denial instead of the form.
  // The market caps ride the spend-splits endpoint (the market-context
  // endpoint built for the doors in Step 4) for the helper text.
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoadingTemplates(true)
    setDenied(null)
    Promise.all([
      api.getPaidAdsCampaignTemplates(LAUNCH_PLATFORM),
      api.getPaidAdsSpendSplits(LAUNCH_PLATFORM),
    ])
      .then(([templatesRes, splitsRes]) => {
        if (cancelled) return
        const items = templatesRes?.templates || []
        setTemplates(items)
        if (items[0]) setSelectedTemplateId(items[0].id)
        const resolvedMarket = splitsRes?.market || null
        setMarket(resolvedMarket)
        setCountry(resolvedMarket?.country || '')
      })
      .catch((err: any) => {
        if (cancelled) return
        if (err?.status === 402 || err?.status === 403) {
          setDenied({ message: err?.error || err?.message, code: err?.code })
        } else {
          addToast({ title: cdc('door.error.generic', locale), description: err?.message, variant: 'error' })
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingTemplates(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, addToast, locale])

  const capsHelper = useMemo(() => {
    if (!market) return null
    const min = market.min_budget_micros
    const max = market.max_budget_micros
    if (min != null && max != null) return cdc('paid.budget.capsHelper', locale, { country: market.country, min: formatMicros(min), max: formatMicros(max) })
    if (min != null) return cdc('paid.budget.capsMinOnly', locale, { country: market.country, min: formatMicros(min) })
    if (max != null) return cdc('paid.budget.capsMaxOnly', locale, { country: market.country, max: formatMicros(max) })
    return null
  }, [market, locale])

  const chosenTemplate: CampaignTemplate | null = useMemo(
    () => templates.find((template) => template.id === selectedTemplateId) || null,
    [templates, selectedTemplateId],
  )

  function buildTargeting() {
    const territory: Record<string, unknown> = {}
    if (country) territory.country = country.toUpperCase()
    const areaIds = areas.split(',').map((value) => value.trim()).filter(Boolean)
    if (areaIds.length) territory.area_ids = areaIds
    if (radius) territory.radius_meters = Number(radius)
    return Object.keys(territory).length ? { territory } : {}
  }

  /** The Step-5 chooser: the AI proposes, nothing is created by proposing. */
  async function proposeWithAi() {
    setAiProposing(true)
    setAiNotice(null)
    try {
      // H3: the agent's one-line objective draft rides to the AI exactly like
      // the broadcast door — the AI improves it, never invents it.
      const result = await api.createPaidAdsAiProposal({
        door: 'campaign',
        objective_draft: objective.trim() || undefined,
      })
      const proposal = result?.proposal
      if (!proposal) throw new Error('empty proposal')
      if (proposal.objective) setObjective(proposal.objective)
      if (proposal.budget_micros != null) setBudget(String(proposal.budget_micros / 1_000_000))
      const territory = proposal.targeting?.territory
      if (territory?.area_ids?.length) setAreas(territory.area_ids.join(', '))
      if (territory?.radius_meters) setRadius(String(territory.radius_meters))
    } catch (err: any) {
      setAiNotice(cdc('ai.unavailable', locale, { code: err?.code || cdc('door.error.generic', locale) }))
    } finally {
      setAiProposing(false)
    }
  }

  function validate(): boolean {
    const errors: Record<string, string> = {}
    if (avenue === 'predefined') {
      if (!chosenTemplate) errors.template = cdc('template.empty', locale)
    }
    if (avenue === 'ai' || avenue === 'manual') {
      if (!objective.trim()) errors.objective = cdc('door.objective.label', locale)
      if (avenue === 'manual' && !name.trim()) errors.name = cdc('paid.name.label', locale)
      if (!budget || Number.isNaN(Number(budget)) || Number(budget) <= 0) {
        errors.budget = cdc('paid.budget.label', locale)
      }
      // Caps are NOT pre-checked: the server is the final word and lands
      // INVALID_BUDGET inline (the helper text states the range).
    }
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  async function submit() {
    if (!validate()) return
    setCreating(true)
    setFieldErrors({})
    try {
      const payload: Record<string, unknown> = { platform: LAUNCH_PLATFORM }
      if (avenue === 'predefined') {
        payload.template_id = selectedTemplateId
        payload.source = 'manual' // the API forces source=predefined on copy
        if (objective.trim()) payload.objective = objective.trim()
        if (budget) payload.budget_micros = Math.round(Number(budget) * 1_000_000)
        if (budget) payload.currency = 'USD'
      } else {
        payload.source = avenue === 'ai' ? 'ai' : 'manual'
        payload.objective = objective.trim()
        if (avenue === 'manual') payload.name = name.trim()
        if (budget) {
          payload.budget_micros = Math.round(Number(budget) * 1_000_000)
          payload.currency = 'USD'
        }
        payload.targeting = buildTargeting()
      }
      const campaign = await api.createPaidAdsCampaign(payload as Parameters<typeof api.createPaidAdsCampaign>[0])
      setCreated(true)
      onCreated?.({ id: campaign.id, source: campaign.source, status: campaign.status })
    } catch (err: any) {
      if (err?.status === 400 && err?.code) {
        const field = err.code === 'INVALID_BUDGET'
          ? 'budget'
          : err.code === 'CAMPAIGN_TEMPLATE_MARKET_MISMATCH' || err.code === 'CAMPAIGN_TEMPLATE_NOT_FOUND'
            ? 'template'
            : err.code === 'VALIDATION_ERROR'
              ? 'form'
              : 'form'
        setFieldErrors((prev) => ({ ...prev, [field]: err?.error || err?.message }))
      } else if (err?.status === 402 || err?.status === 403) {
        setDenied({ message: err?.error || err?.message, code: err?.code })
      } else {
        addToast({ title: cdc('door.error.generic', locale), description: err?.message, variant: 'error' })
      }
    } finally {
      setCreating(false)
    }
  }

  const avenueOption = (value: Avenue) => (
    <label
      key={value}
      className={`flex cursor-pointer items-start gap-3 rounded-[var(--lc-radius-md)] border p-3 transition-colors ${
        avenue === value
          ? 'border-[var(--lc-border-strong)] bg-[var(--lc-surface-sunken)]'
          : 'border-[var(--lc-border)] bg-[var(--lc-surface)]'
      }`}
      data-testid={`avenue-${value}`}
    >
      <input
        type="radio"
        name="campaign-avenue"
        value={value}
        checked={avenue === value}
        onChange={() => setAvenue(value)}
        className="mt-1"
      />
      <span>
        <span className="block font-medium text-[var(--lc-text-primary)]">
          {cdc(`avenue.${value}.label`, locale)}
        </span>
        <span className="block text-sm text-[var(--lc-text-muted)]">{cdc(`avenue.${value}.hint`, locale)}</span>
      </span>
    </label>
  )

  const fieldError = (field: string) =>
    fieldErrors[field] ? <DoorFieldError error={fieldErrors[field]} testId={`error-${field}`} /> : null

  const targetingLabels = {
    country: cdc('paid.targeting.country.label', locale),
    countryHelper: market?.country
      ? cdc('paid.targeting.country.helper', locale, { country: market.country })
      : undefined,
    areas: cdc('paid.targeting.areas.label', locale),
    areasPlaceholder: cdc('paid.targeting.areas.placeholder', locale),
    radius: cdc('paid.targeting.radius.label', locale),
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) reset(); onOpenChange(next) }}>
      <DialogContent className="max-w-xl" data-testid="campaign-door-dialog">
        <DialogHeader>
          <DialogTitle>{cdc('door.title', locale)}</DialogTitle>
          <DialogDescription>{cdc('door.description', locale)}</DialogDescription>
        </DialogHeader>

        {loadingTemplates ? (
          <div className="flex items-center gap-2 py-6 text-sm text-[var(--lc-text-muted)]">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> …
          </div>
        ) : denied ? (
          <DoorDeniedState
            title={cdc('door.denied.title', locale)}
            message={denied.message}
            code={denied.code}
            upsell={cdc('door.denied.upsell', locale)}
            testId="campaign-denied"
          />
        ) : created ? (
          <div
            role="status"
            className="space-y-2 rounded-[var(--lc-radius-md)] border border-[var(--lc-border)] bg-[var(--lc-surface-sunken)] p-4"
            data-testid="campaign-created"
          >
            <p className="flex items-center gap-2 font-medium text-[var(--lc-text-heading)]">
              <Rocket className="h-4 w-4" aria-hidden />
              {cdc('door.created.title', locale)}
            </p>
            <p className="text-sm text-[var(--lc-text-muted)]">{cdc('door.created.body', locale)}</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2" role="radiogroup" aria-label={cdc('door.title', locale)}>
              {avenueOption('predefined')}
              {avenueOption('ai')}
              {avenueOption('manual')}
            </div>

            {avenue === 'predefined' ? (
              <div className="space-y-2" data-testid="template-section">
                {templates.length ? (
                  templates.map((template) => (
                    <label
                      key={template.id}
                      className={`flex cursor-pointer items-start gap-3 rounded-[var(--lc-radius-md)] border p-3 ${
                        selectedTemplateId === template.id
                          ? 'border-[var(--lc-border-strong)] bg-[var(--lc-surface-sunken)]'
                          : 'border-[var(--lc-border)] bg-[var(--lc-surface)]'
                      }`}
                    >
                      <input
                        type="radio"
                        name="campaign-template"
                        checked={selectedTemplateId === template.id}
                        onChange={() => setSelectedTemplateId(template.id)}
                      />
                      <span>
                        <span className="block text-sm font-medium text-[var(--lc-text-primary)]">
                          {template.name}
                        </span>
                        <span className="block text-sm text-[var(--lc-text-muted)]">{template.objective}</span>
                        {template.recommended_budget_micros != null ? (
                          <span className="block text-xs text-[var(--lc-text-muted)]">
                            {cdc('template.recommended', locale)}: {formatMicros(template.recommended_budget_micros)}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  ))
                ) : (
                  <p className="text-sm text-[var(--lc-text-muted)]">{cdc('template.empty', locale)}</p>
                )}
                {fieldError('template')}
                {/* the agent still defines the objective (optional override) */}
                <div className="space-y-2 pt-1">
                  <Label htmlFor="campaign-objective">{cdc('door.objective.label', locale)}</Label>
                  <Input
                    id="campaign-objective"
                    value={objective}
                    onChange={(event) => setObjective(event.target.value)}
                    placeholder={chosenTemplate?.objective || cdc('door.objective.placeholder', locale)}
                    className="bg-[var(--lc-surface-sunken)]"
                  />
                </div>
              </div>
            ) : null}

            {avenue === 'ai' || avenue === 'manual' ? (
              <div className="space-y-3 rounded-[var(--lc-radius-md)] border border-[var(--lc-border)] bg-[var(--lc-surface)] p-3">
                {avenue === 'ai' ? (
                  <div className="space-y-2" data-testid="ai-section">
                    <p className="text-xs text-[var(--lc-text-muted)]">{cdc('ai.helper', locale)}</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={proposeWithAi}
                      disabled={aiProposing}
                      data-testid="ai-propose"
                    >
                      {aiProposing ? <Loader2 className="me-2 h-4 w-4 animate-spin" aria-hidden /> : null}
                      {aiProposing ? cdc('ai.proposing', locale) : cdc('ai.propose', locale)}
                    </Button>
                    {aiNotice ? (
                      <p role="alert" className="text-sm text-[var(--lc-status-underOffer-fg)]" data-testid="ai-notice">
                        {aiNotice}
                      </p>
                    ) : null}
                  </div>
                ) : null}

                {avenue === 'manual' ? (
                  <div className="space-y-2">
                    <Label htmlFor="campaign-name">{cdc('paid.name.label', locale)}</Label>
                    <Input
                      id="campaign-name"
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                      placeholder={cdc('paid.name.placeholder', locale)}
                      className="bg-[var(--lc-surface-sunken)]"
                    />
                    {fieldError('name')}
                  </div>
                ) : null}

                <div className="space-y-2">
                  <Label htmlFor="campaign-objective">{cdc('door.objective.label', locale)}</Label>
                  <Input
                    id="campaign-objective"
                    value={objective}
                    onChange={(event) => setObjective(event.target.value)}
                    placeholder={cdc('door.objective.placeholder', locale)}
                    className="bg-[var(--lc-surface-sunken)]"
                  />
                  {fieldError('objective')}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="campaign-budget">{cdc('paid.budget.label', locale)}</Label>
                  <Input
                    id="campaign-budget"
                    inputMode="decimal"
                    value={budget}
                    onChange={(event) => setBudget(event.target.value)}
                    className="bg-[var(--lc-surface-sunken)]"
                  />
                  {capsHelper ? <p className="text-xs text-[var(--lc-text-muted)]">{capsHelper}</p> : null}
                  {fieldError('budget')}
                </div>

                <DoorTargetingFields
                  country={country}
                  onCountryChange={setCountry}
                  areas={areas}
                  onAreasChange={setAreas}
                  radius={radius}
                  onRadiusChange={setRadius}
                  labels={targetingLabels}
                />
              </div>
            ) : null}
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => { reset(); onOpenChange(false) }}
          >
            {cdc('door.cancel', locale)}
          </Button>
          {avenue && !denied && !created ? (
            <Button type="button" onClick={submit} disabled={creating} data-testid="campaign-submit">
              {creating ? <Loader2 className="me-2 h-4 w-4 animate-spin" aria-hidden /> : null}
              {creating ? cdc('door.submit.creating', locale) : cdc(`door.submit.${avenue}`, locale)}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
